import type { RoofVolumeHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  Arrangement,
  GeneratedRoofVolume,
  ImportHalfEdge,
  ImportVertex,
  RoofHalfEdge,
  RoofVertex,
  RoofVolume,
} from "building-types/roofVolumes/roofVolumeTypes";
import { docBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import type { Face } from "geom/brep";
import { getFaceHalfEdges, halfEdgesToFace } from "../elements/roofVolumes/arrangementUtils";
import roofAutoComplete from "../elements/roofVolumes/roofAutoComplete";

export function makeRoofVolume(roofVolume: RoofVolume): GeneratedRoofVolume {
  const importArrangement = roofVolume.arrangement;
  let faces: Face[] = [];

  if (importArrangement) {
    // Convert from the import half-edges and vertices to the roof half-edges and vertices.
    // They are roughly the same except the import version uses indices.
    const halfEdges = importArrangement.halfEdges.map((half: ImportHalfEdge): RoofHalfEdge => {
      return { convex: half.convex };
    });
    const dataVertices = roofVolume.arrangement.vertices.map((vertex: ImportVertex, index: number): RoofVertex => {
      const hIndices = importArrangement.vertices[index].halfEdges;
      const vHalfs = hIndices.map((hIndex) => halfEdges[hIndex]);
      return { ...vertex, halfEdges: vHalfs };
    });
    halfEdges.forEach((halfEdge, index) => {
      const importHalf = importArrangement.halfEdges[index];
      halfEdge.origin = dataVertices[importHalf.origin];
      if (importHalf.twin >= 0) {
        halfEdge.twin = halfEdges[importHalf.twin];
      }
      if (importHalf.next >= 0) {
        halfEdge.next = halfEdges[importHalf.next];
      }
      if (importHalf.prev >= 0) {
        halfEdge.prev = halfEdges[importHalf.prev];
      }
    });
    const vertices = importArrangement.vIndices.map((index) => dataVertices[index]);
    const arrangement = { halfEdges, vertices };
    roofAutoComplete(arrangement);
    faces = generateFaces(arrangement);
  }

  const roofVolumeHandle: RoofVolumeHandle = { type: "ROOF_VOLUME", id: roofVolume.id };

  return {
    ...docBuildingEntityBaseNew("ROOF_VOLUME", roofVolume.id, roofVolumeHandle),
    faces,
  };
}

// Generate the faces from a given list of half-edges.
function generateFaces(arrangement: Arrangement): Face[] {
  const allFaceHalfEdges = getFaceHalfEdges(arrangement);
  const faces: Face[] = [];
  allFaceHalfEdges.forEach((faceHalfEdges) => {
    const face = halfEdgesToFace(faceHalfEdges);
    face && faces.push(face);
  });

  return faces;
}
